import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Sidebar from '@/components/Sidebar';
import MobileNav from '@/components/MobileNav';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, FileText, Receipt } from 'lucide-react';
import { getExpenses, getEvents } from '@/lib/api';
import { toast } from 'sonner';

const Expenses = ({ user }) => {
  const [expenses, setExpenses] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterEvent, setFilterEvent] = useState('all');

  const handleFilterChange = (value) => {
    setFilterEvent(value);
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [expensesRes, eventsRes] = await Promise.all([
        getExpenses(),
        getEvents()
      ]);
      setExpenses(expensesRes.data);
      setEvents(eventsRes.data);
    } catch (error) {
      toast.error('Failed to load expenses');
    } finally {
      setLoading(false);
    }
  };

  const filteredExpenses = filterEvent && filterEvent !== 'all'
    ? expenses.filter(e => e.event_id === filterEvent)
    : expenses;

  const totalExpenses = filteredExpenses.reduce((sum, e) => sum + e.amount, 0);

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      <Sidebar user={user} />
      
      <div className="flex-1 flex flex-col overflow-hidden md:ml-64">
        <header className="bg-white border-b border-border px-6 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
            <div>
              <h2 className="text-2xl font-bold text-foreground" style={{ fontFamily: 'Manrope, sans-serif' }}>Expenses</h2>
              <p className="text-sm text-muted-foreground">View all expense records</p>
            </div>
            <Link to="/expenses/new">
              <Button data-testid="new-expense-button" className="shadow-sm">
                <Plus className="h-4 w-4 mr-2" />
                New Expense
              </Button>
            </Link>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-6 pb-24 md:pb-6">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : (
            <div className="max-w-6xl mx-auto space-y-6">
              {/* Filter and Summary */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="md:col-span-2">
                  <CardContent className="p-4">
                    <div className="flex items-center space-x-3">
                      <label className="text-sm font-medium text-foreground">Filter by Event:</label>
                      <Select value={filterEvent} onValueChange={handleFilterChange}>
                        <SelectTrigger className="w-full max-w-xs" data-testid="filter-event-select">
                          <SelectValue placeholder="All Events" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Events</SelectItem>
                          {events.map((event) => (
                            <SelectItem key={event.event_id} value={event.event_id}>
                              {event.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="bg-gradient-to-br from-primary/10 to-accent/20">
                  <CardContent className="p-4">
                    <p className="text-sm text-muted-foreground mb-1">Total Expenses</p>
                    <p className="text-3xl font-bold text-primary" data-testid="total-expenses-amount">${totalExpenses.toFixed(2)}</p>
                    <p className="text-xs text-muted-foreground mt-1">{filteredExpenses.length} transactions</p>
                  </CardContent>
                </Card>
              </div>

              {/* Expenses List */}
              {filteredExpenses.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-64 text-center">
                  <FileText className="h-16 w-16 text-muted-foreground mb-4" />
                  <h3 className="text-xl font-semibold text-foreground mb-2">No expenses found</h3>
                  <p className="text-muted-foreground mb-6">Start logging your expenses</p>
                  <Link to="/expenses/new">
                    <Button>
                      <Plus className="h-4 w-4 mr-2" />
                      Log Expense
                    </Button>
                  </Link>
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredExpenses.map((expense) => (
                    <Card key={expense.expense_id} className="card-hover" data-testid={`expense-card-${expense.expense_id}`}>
                      <CardContent className="p-6">
                        <div className="flex flex-col md:flex-row md:items-start md:justify-between space-y-3 md:space-y-0">
                          <div className="flex-1">
                            <div className="flex items-start space-x-3">
                              <div className="p-2 bg-primary/10 rounded-lg">
                                <Receipt className="h-5 w-5 text-primary" />
                              </div>
                              <div className="flex-1">
                                <h3 className="font-bold text-lg text-foreground mb-1">{expense.description}</h3>
                                <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                                  <Badge variant="outline">{expense.category}</Badge>
                                  <span>·</span>
                                  <span>{expense.event_name}</span>
                                  {expense.vendor && (
                                    <>
                                      <span>·</span>
                                      <span>Paid to: {expense.vendor}</span>
                                    </>
                                  )}
                                </div>
                                <p className="text-sm text-muted-foreground mt-2">
                                  Logged by {expense.logged_by_name} · {new Date(expense.created_at).toLocaleDateString()}
                                </p>
                              </div>
                            </div>
                          </div>
                          
                          <div className="flex items-center justify-between md:justify-end space-x-4">
                            {expense.receipt_data && (
                              <Badge variant="secondary" className="bg-emerald-100 text-emerald-800">
                                Receipt
                              </Badge>
                            )}
                            <p className="text-2xl font-bold text-primary">${expense.amount.toFixed(2)}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          )}
        </main>

        <MobileNav user={user} />
      </div>
    </div>
  );
};

export default Expenses;
